import type { LintConfig, StructuredLintConfig } from "harper.js";

/**
 * Fiction drafting defaults for Harper.
 *
 * Both filters here are deliberately **fail-open**: we name the noisy groups
 * and kinds we want silenced, and leave everything Harper reports otherwise.
 * An allowlist would silently kill spelling entirely whenever upstream renames
 * a group or kind, which is indistinguishable from "Harper is broken".
 */

/** Rule groups muted for prose. Unknown groups keep Harper's own default. */
export const FICTION_MUTED_GROUPS = new Set([
  // "Try to avoid offensive language", 227 rules of word-choice nagging.
  "Word Choice and Usage",
  // Redundancy/"boring words" style advice.
  "Style and Redundancy",
  // Expands ASAP/FYI etc. Wrong for dialogue.
  "Initialisms",
  // Brand/product capitalization. Irrelevant to a manuscript.
  "Proper Nouns",
  // Fiction uses dialect on purpose.
  "Regionalisms and Dialect",
]);

/** Lint kinds dropped before they reach the editor. */
export const FICTION_MUTED_KINDS = new Set([
  "Style",
  "Enhancement",
  "Readability",
  "WordChoice",
  "Regionalism",
  "Repetition",
  "Redundancy",
  // Plaintext mode flags leading indent as "French spaces".
  "Formatting",
]);

/**
 * Named rules we always disable, even if Harper regroups them.
 * AvoidCurses is the "change this swear word" rule.
 * NoFrenchSpaces fires on paragraph indents under plaintext mode.
 */
export const FICTION_LINT_RULE_OVERRIDES: LintConfig = {
  AvoidCurses: false,
  BoringWords: false,
  AvoidContractions: false,
  RepeatedWords: false,
  UseEllipsisCharacter: false,
  LitotesDirectPositive: false,
  NoFrenchSpaces: false,
};

/** False only for kinds we positively recognize as noise. */
export function isFictionLintKind(kind: string): boolean {
  return FICTION_MUTED_KINDS.has(kind) === false;
}

/**
 * Walk Harper's structured config and disable the muted groups.
 *
 * Rules outside those groups are left out of the returned config entirely, so
 * they keep Harper's default state. Never returns a config that disables
 * spelling.
 */
export function buildFictionLintConfig(
  structured: StructuredLintConfig,
): LintConfig {
  const config: LintConfig = {};

  function visit(
    settings: StructuredLintConfig["settings"],
    muted: boolean,
  ): void {
    for (const item of settings) {
      if ("Group" in item) {
        visit(
          item.Group.child.settings,
          muted || FICTION_MUTED_GROUPS.has(item.Group.label),
        );
        continue;
      }
      if (muted === false) continue;
      if ("Bool" in item) {
        config[item.Bool.name] = false;
        continue;
      }
      if ("OneOfMany" in item) {
        for (const name of item.OneOfMany.names) config[name] = false;
      }
    }
  }

  visit(structured.settings, false);
  return { ...config, ...FICTION_LINT_RULE_OVERRIDES };
}
